/**
 * The physics of carrying: while the store says an object is carried, its body is
 * switched to position-kinematic and *pursues* a hold point in front of the camera —
 * an exponential chase, not a teleport — with its yaw easing toward the view so the
 * box faces the player throughout.
 *
 * Anything stacked on the grabbed box comes along: at grab time the resting pile is
 * detected (by cuboid overlap, recursively upward) and each rider becomes a kinematic
 * *follower*, keeping its pose relative to the carried box every frame. Riders are
 * sensors while carried — they cannot shove the player or catch on frames — and on
 * throw or drop they are released together with the carried box, inheriting its
 * launch velocity. The carried box itself stays solid toward the world (the player
 * controller and rays skip it by id) but stops colliding with fellow carryables.
 *
 * A forward **shape** cast of the box's own cuboid pulls the hold point closer when the
 * way ahead is blocked, so the box yields toward the player instead of clipping through
 * geometry. It used to be a ray, which measured only where the box's *centre* could go
 * and so let half a box stand inside a wall — worse near a jamb or a corner, where the
 * ray found nothing at all and the box swung clean through.
 *
 * Interact is consumed here only while carrying — and not even then when what is in hand
 * is a part of what is in the crosshair: that press is an attach, and belongs to
 * `InteractionSystem` (design D4). `InteractionSystem` owns it otherwise.
 */

import { useFrame, useThree } from '@react-three/fiber';
import { useRapier, type RapierRigidBody } from '@react-three/rapier';
import { useRef } from 'react';
import { Quaternion, Vector3 } from 'three';
import {
  carryCeiling,
  HOLD_DISTANCE,
  MIN_HOLD_DISTANCE,
  THROW_SPEED,
  THROW_UPWARD_BIAS,
  throwAngularVelocity,
} from '../game/carrying';
import { availableAction } from '../game/interaction';
import { isPlayable } from '../game/phase';
import { recordObjectHome } from './objectHomes';
import { consumeInteract, consumeThrow, inputState } from '../input/inputState';
import { levelById } from '../game/progression';
import { useGameStore } from '../store/gameStore';
import {
  CARRIED_GROUPS,
  CARRYABLE_GROUPS,
  cuboidHalfExtents,
  forEachObjectBody,
  getObjectBody,
} from './objectRegistry';
import type { BodyUserData } from './useInteractionTarget';

/**
 * Kept between the box's own surface and whatever the cast hits. Smaller than the old
 * ray's clearance, because the cast already accounts for the box's size — this is a
 * skin, not a stand-in for half a box.
 */
const HOLD_CLEARANCE = 0.06;
/** How aggressively the carried box chases the hold pose, per second. */
const PURSUIT_RATE = 14;
/** How closely a box must sit on another's top face to count as stacked on it. */
const STACK_TOLERANCE = 0.15;

type Follower = {
  id: string;
  body: RapierRigidBody;
  /** Pose relative to the carried box at grab time. */
  offset: Vector3;
  rotation: Quaternion;
};

function isRestingOn(top: RapierRigidBody, base: RapierRigidBody): boolean {
  const topHalf = cuboidHalfExtents(top);
  const baseHalf = cuboidHalfExtents(base);
  const topPosition = top.translation();
  const basePosition = base.translation();

  const horizontallyOverlapping =
    Math.abs(topPosition.x - basePosition.x) <= (topHalf.x + baseHalf.x) * 0.95 &&
    Math.abs(topPosition.z - basePosition.z) <= (topHalf.z + baseHalf.z) * 0.95;
  if (!horizontallyOverlapping) return false;

  const baseTop = basePosition.y + baseHalf.y;
  const topBottom = topPosition.y - topHalf.y;
  return Math.abs(topBottom - baseTop) <= STACK_TOLERANCE;
}

/** The pile resting on the carried box, found by walking upward layer by layer. */
function findRiders(carriedId: string, carriedBody: RapierRigidBody): Follower[] {
  const followers: Follower[] = [];
  const bases: RapierRigidBody[] = [carriedBody];
  const taken = new Set<string>([carriedId]);
  const carriedPosition = carriedBody.translation();
  const carriedRotation = carriedBody.rotation();
  const inverseCarried = new Quaternion(
    carriedRotation.x,
    carriedRotation.y,
    carriedRotation.z,
    carriedRotation.w,
  ).invert();

  while (bases.length > 0) {
    const base = bases.pop()!;
    forEachObjectBody((id, candidate) => {
      if (taken.has(id) || !isRestingOn(candidate, base)) return;
      taken.add(id);
      bases.push(candidate);

      const position = candidate.translation();
      const rotation = candidate.rotation();
      followers.push({
        id,
        body: candidate,
        offset: new Vector3(
          position.x - carriedPosition.x,
          position.y - carriedPosition.y,
          position.z - carriedPosition.z,
        ).applyQuaternion(inverseCarried),
        rotation: inverseCarried
          .clone()
          .multiply(new Quaternion(rotation.x, rotation.y, rotation.z, rotation.w)),
      });
    });
  }

  return followers;
}

export function CarryController(): null {
  const camera = useThree((state) => state.camera);
  const { world, rapier } = useRapier();

  const directionRef = useRef(new Vector3());
  const holdPointRef = useRef(new Vector3());
  const currentRef = useRef(new Vector3());
  const targetRotationRef = useRef(new Quaternion());
  const currentRotationRef = useRef(new Quaternion());
  const followerPositionRef = useRef(new Vector3());
  const followerRotationRef = useRef(new Quaternion());
  /** The carried box's own shape, reused for the forward cast; resized at each grab. */
  const shapeRef = useRef<InstanceType<typeof rapier.Cuboid> | null>(null);
  const throwCountRef = useRef(0);
  const followersRef = useRef<Follower[]>([]);
  /** Height from the carried box's centre to the top of the stack riding on it. */
  const stackTopRef = useRef(0);

  useFrame((_, delta) => {
    const store = useGameStore.getState();
    if (!isPlayable(store.phase)) return;

    const carriedId = store.carriedObjectId;
    if (carriedId === null) return;
    const body = getObjectBody(carriedId);
    if (!body) return;

    // Idempotent grab: the frame after pick-up, the body becomes ours to place, and
    // whatever rests on it is enlisted to follow.
    if (body.bodyType() !== rapier.RigidBodyType.KinematicPositionBased) {
      followersRef.current = findRiders(carriedId, body);
      body.setBodyType(rapier.RigidBodyType.KinematicPositionBased, true);
      body.collider(0).setCollisionGroups(CARRIED_GROUPS);
      const half = cuboidHalfExtents(body);
      stackTopRef.current = half.y;
      // Sized once per grab, then reused: the cast runs every frame and must not
      // allocate. Boxes differ in size, so it cannot be a module constant.
      shapeRef.current ??= new rapier.Cuboid(half.x, half.y, half.z);
      shapeRef.current.halfExtents = { x: half.x, y: half.y, z: half.z };
      for (const follower of followersRef.current) {
        follower.body.setBodyType(rapier.RigidBodyType.KinematicPositionBased, true);
        // Riders are sensors while carried: never an obstacle, never a crushed contact.
        follower.body.collider(0).setSensor(true);
        stackTopRef.current = Math.max(
          stackTopRef.current,
          follower.offset.y + cuboidHalfExtents(follower.body).y,
        );
      }
    }

    const direction = camera.getWorldDirection(directionRef.current);

    const releaseAll = (mode: 'throw' | 'drop') => {
      releaseBody(carriedId, body, direction, mode, throwCountRef.current++, rapier.RigidBodyType.Dynamic);
      for (const follower of followersRef.current) {
        follower.body.collider(0).setSensor(false);
        // Riders always detach gently — hurling the whole stack squeezes the top box
        // between the launched one and the ceiling until the solver pops it through
        // the roof. Throwing hurls what is in the hands; the pile tumbles off.
        releaseBody(
          follower.id,
          follower.body,
          direction,
          'drop',
          throwCountRef.current++,
          rapier.RigidBodyType.Dynamic,
        );
      }
      followersRef.current = [];
      store.release();
    };

    // Whose press is it? While what is held fits what is looked at, the press means
    // attach and belongs to `InteractionSystem`, which consumes both flags itself. Asking
    // the same pure question, of the same store state, in the same frame is what keeps
    // the two systems from disagreeing — mount order must not decide it (design D4), and
    // the loser reads no flag at all, so a press is never consumed twice or swallowed by
    // a system that then declines to act on it.
    const attaching =
      availableAction(store.focus, {
        carriedObjectId: carriedId,
        carriedObjectKind: store.carriedObjectKind,
        doorStates: store.doorStates,
        roomLights: store.roomLights,
        attachments: store.attachments,
      })?.verb === 'attach';

    // Both are read every frame, never one or the other: a desktop left-click sets throw
    // *and* interact, because the producer cannot know whether the hands are full.
    // Leaving interact pending after a throw would re-grab the box on the next frame.
    if (!attaching) {
      const throwing = consumeThrow(inputState);
      const dropping = consumeInteract(inputState);

      if (throwing) {
        releaseAll('throw');
        return;
      }
      if (dropping) {
        releaseAll('drop');
        return;
      }
    }
    // Carrying carries on below either way. Returning outright while attach is on offer
    // would be simpler, but it stops the pursuit: the held part would hang still in the
    // world for as long as the player kept the host in the crosshair.

    // Face the player: yaw only, staying upright. Computed before the cast because the
    // box is swept in the pose it will actually be held in — a wide box turned edge-on
    // fits where the same box turned flat does not.
    const viewYaw = Math.atan2(-direction.x, -direction.z);
    const heldRotation = targetRotationRef.current.setFromAxisAngle(UP, viewYaw);

    // Sweep the box itself from the eye along the view. The predicate skips sensors, the
    // player and the carried box, so the cast sees exactly the world the box could hit.
    const shape = shapeRef.current;
    const hit =
      shape &&
      world.castShape(
        camera.position,
        heldRotation,
        direction,
        shape,
        0,
        HOLD_DISTANCE,
        // Report a contact even if the sweep starts already overlapping — pressed into a
        // corner that is the normal case, and ignoring it would hand back "no hit" and
        // put the box at full arm's length inside the wall.
        true,
        undefined,
        undefined,
        undefined,
        undefined,
        (collider) => {
          if (collider.isSensor()) return false;
          const data = collider.parent()?.userData as Partial<BodyUserData> | undefined;
          if (data?.kind === 'player') return false;
          return !(data?.kind === 'carryable' && data.id === carriedId);
        },
      );

    // `time_of_impact` is how far the box's centre may travel before its surface touches
    // something, so the clearance below is a skin on top of the box, not a guess at half
    // its size the way the old ray needed.
    const distance = hit
      ? Math.max(MIN_HOLD_DISTANCE, Math.min(HOLD_DISTANCE, hit.time_of_impact - HOLD_CLEARANCE))
      : HOLD_DISTANCE;

    const holdPoint = holdPointRef.current
      .copy(direction)
      .multiplyScalar(distance)
      .add(camera.position);

    // Keep the whole stack under the ceiling: riders are sensors, and a rider held
    // inside the roof slab would be released inside it and escape the house. Only where
    // there *is* a ceiling — a maze is open sky, and clamping to a room's height there
    // left what the player was holding hanging inside a hedge as they climbed past it.
    const level = store.profile && store.currentLevelId ? levelById(store.profile, store.currentLevelId) : null;
    const ceilingHeight = carryCeiling(level);
    if (ceilingHeight !== null) {
      holdPoint.y = Math.min(holdPoint.y, ceilingHeight - stackTopRef.current - 0.05);
    }

    // Exponential pursuit: frame-rate independent, bounded per-step displacement.
    const alpha = 1 - Math.exp(-PURSUIT_RATE * delta);
    const translation = body.translation();
    const next = currentRef.current
      .set(translation.x, translation.y, translation.z)
      .lerp(holdPoint, alpha);
    body.setNextKinematicTranslation(next);

    // Ease into the pose the cast above already assumed.
    const rotation = body.rotation();
    const nextRotation = currentRotationRef.current
      .set(rotation.x, rotation.y, rotation.z, rotation.w)
      .slerp(heldRotation, alpha);
    body.setNextKinematicRotation(nextRotation);

    // Riders keep their grab-time pose relative to the carried box.
    for (const follower of followersRef.current) {
      const position = followerPositionRef.current
        .copy(follower.offset)
        .applyQuaternion(nextRotation)
        .add(next);
      follower.body.setNextKinematicTranslation(position);
      follower.body.setNextKinematicRotation(
        followerRotationRef.current.copy(nextRotation).multiply(follower.rotation),
      );
    }
  });

  return null;
}

const UP = new Vector3(0, 1, 0);

function releaseBody(
  id: string,
  body: RapierRigidBody,
  direction: Vector3,
  mode: 'throw' | 'drop',
  throwIndex: number,
  dynamicType: Parameters<RapierRigidBody['setBodyType']>[0],
): void {
  // Where it comes back to if it leaves the world (design D7). Recorded here because this
  // is the one function every throw and every drop goes through, riders included, so "the
  // pose the player last had it at" needs no other call site. `recordObjectHome` refuses a
  // pose that is not over the grass, which is what stops a throw from the crest out over
  // the moat becoming a home the object falls out of forever (design D8).
  recordObjectHome(id, body);

  // Dynamic first, then velocities — velocities set on a kinematic body are discarded.
  body.setBodyType(dynamicType, true);
  body.collider(0).setCollisionGroups(CARRYABLE_GROUPS);

  if (mode === 'throw') {
    body.setLinvel(
      {
        x: direction.x * THROW_SPEED,
        y: direction.y * THROW_SPEED + THROW_UPWARD_BIAS,
        z: direction.z * THROW_SPEED,
      },
      true,
    );
    body.setAngvel(throwAngularVelocity(throwIndex), true);
    return;
  }

  body.setLinvel({ x: 0, y: 0, z: 0 }, true);
  body.setAngvel({ x: 0, y: 0, z: 0 }, true);
}
